// @ts-strict-ignore
import React, { useState } from "react";

import { BodyText, FlexBox, Heading, StatusText } from "@braze/beacon-core";
import { LoadingSpinner } from "@braze/beacon-labs-spinner";
import { getSpacing } from "@braze/beacon-styling";
import { useI18nLocale } from "@braze/i18n-core";

import useI18nNamespace from "@services/useI18nNamespace";

import { useSubmitRegistrationData } from "@scenes/FreeTrial/hooks";

import type { VerificationSuccessProps } from "./types";

const successIcon = "https://cdn.braze.com/dashboard-assets/dashboard/plg-assets/success_state_logo.svg";

export const VerificationSuccess = ({
  businessDetails,
  prospectDetails,
  marketoParams,
}: VerificationSuccessProps): React.ReactElement => {
  const { t } = useI18nNamespace("free-trial-registration");
  const currentLocale = useI18nLocale();

  const [errorMessage, setErrorMessage] = useState<string>();
  const [success, setSuccess] = useState<boolean>(false);
  const { mutate, isLoading, status } = useSubmitRegistrationData({
    onError: setErrorMessage,
    onSuccess: () => setSuccess(true),
  });
  if (!success && !errorMessage && !isLoading && status === "idle") {
    mutate({
      email: prospectDetails.email,
      phone_number: prospectDetails.phoneNumber,
      first_name: prospectDetails.firstName,
      last_name: prospectDetails.lastName,
      department: prospectDetails.department,
      job_title: prospectDetails.jobTitle,
      company: prospectDetails.company,
      company_size: businessDetails.companySize,
      country: businessDetails.country,
      industry: businessDetails.industry,
      company_users_count: businessDetails.usersCount,
      business_goals: businessDetails.goals,
      channels_interested: businessDetails.channels,
      features_interested: businessDetails.features,
      locale: currentLocale,

      utm_program_medium_most_recent: marketoParams.INT_UTM_Medium_Most_Recent__c,
      utm_program_source_most_recent: marketoParams.INT_UTM_Source_Most_Recent__c,
      utm_program_campaign_most_recent: marketoParams.INT_UTM_Campaign_Most_Recent__c,
      utm_program_content_most_recent: marketoParams.INT_UTM_Content_Most_Recent__c,
      utm_program_term_most_recent: marketoParams.INT_UTM_Term_Most_Recent__c,
      utm_conversion_page_most_recent: marketoParams.INT_Conversion_Page_Most_Recent__c,
      utm_conversion_date_most_recent: marketoParams.INT_Conversion_Date_Most_Recent__c,
      utm_medium: marketoParams.utm_medium,
      utm_source: marketoParams.utm_source,
      utm_campaign: marketoParams.utm_campaign,
      utm_content: marketoParams.utm_content,
      utm_term: marketoParams.utm_term,

      gclid: marketoParams.GCLID__c,
      email_subscribe: businessDetails.optInMarketing,
    });
  }

  if (errorMessage) {
    return (
      <FlexBox justifyContent="center" alignItems="center" direction="column" my="xxl">
        <Heading level={3}>
          <StatusText status="danger">{errorMessage}</StatusText>
        </Heading>
      </FlexBox>
    );
  }

  if (isLoading) {
    return (
      <FlexBox justifyContent="center" alignItems="center" direction="column">
        <LoadingSpinner />
      </FlexBox>
    );
  }

  if (success) {
    return (
      <FlexBox direction="column" data-cy="completeStatusContainer" id="registrationCompleted">
        <FlexBox
          direction="column"
          alignItems="center"
          justifyContent="center"
          gap={getSpacing("xl") as unknown as string}
          my="xl"
        >
          <img src={successIcon} alt="success-icon" />
          <Heading data-cy="completeStepHeading" level={2}>
            {t("verification-success-heading", "You’re all set!")}
          </Heading>
        </FlexBox>
        <FlexBox justifyContent="flex-start" direction="column" gap={getSpacing("lg") as unknown as string}>
          <BodyText data-cy="completeStepInfo">
            {t(
              "verification-success-message-1",
              "Be on the lookout for an email from Braze with information on how to activate your account."
            )}
          </BodyText>
          <BodyText>
            {t("verification-success-message-2", "Just a few more steps and you’ll be exploring Braze.")}
          </BodyText>
          <Heading level={5}>{t("verification-email-not-received-heading", "Haven’t received the email?")}</Heading>
          <BodyText>
            <ul>
              <li>
                {t("verification-email-not-received-list-1", "It may take a few minutes for the email to arrive.")}
              </li>
              <li>{t("verification-email-not-received-list-2", "Check your spam folder.")}</li>
              <li>
                {t("verification-email-not-received-list-3", "Try searching for the subject 'Welcome to Braze!'")}
              </li>
            </ul>
          </BodyText>
        </FlexBox>
      </FlexBox>
    );
  }
  return (
    <FlexBox justifyContent="center" alignItems="center">
      <LoadingSpinner />
    </FlexBox>
  );
};
